import * as React from 'react'
import { AlertTriangle, Loader2, Play } from 'lucide-react'
import {
  useExecutePostgresQueryMutation,
  type PostgresQueryResult
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import { ConfirmDialog } from '../ConfigurePage/components/DeleteConfirmDialog'
import { cn } from '../../lib/utils'

const QUERY_STORAGE_KEY = 'ontology.settings.postgresQuery.query'
const QUERY_PERSIST_DEBOUNCE_MS = 500
const RESULT_DISPLAY_CAP = 1_000

function loadStored(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function persistStored(key: string, value: string): void {
  try {
    if (value.length === 0) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // best-effort
  }
}

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Unknown error.'
  const obj = error as Record<string, unknown>
  if (obj['data'] && typeof obj['data'] === 'object') {
    const data = obj['data'] as Record<string, unknown>
    if (typeof data['message'] === 'string') return data['message']
    if (typeof data['error'] === 'string') return data['error']
  }
  if (typeof obj['error'] === 'string') return obj['error']
  if (typeof obj['message'] === 'string') return obj['message']
  return 'Unknown error.'
}

/**
 * Settings → Advanced → Databases → Postgres query engine.
 * Runs SQL against the proxy's own ontology Postgres via
 * `POST /maintenance/postgres-query` — no connection credentials needed.
 */
export function PostgresQueryTab({ embedded = false }: { embedded?: boolean }) {
  const [query, setQuery] = React.useState(() => loadStored(QUERY_STORAGE_KEY))
  const [writeMode, setWriteMode] = React.useState(false)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [result, setResult] = React.useState<PostgresQueryResult | null>(null)
  const [error, setError] = React.useState('')

  const [executeQuery, executeState] = useExecutePostgresQueryMutation()

  React.useEffect(() => {
    const id = window.setTimeout(
      () => persistStored(QUERY_STORAGE_KEY, query),
      QUERY_PERSIST_DEBOUNCE_MS
    )
    return () => window.clearTimeout(id)
  }, [query])

  const canRun = query.trim().length > 0 && !executeState.isLoading

  const run = React.useCallback(async () => {
    const trimmed = query.trim()
    if (!trimmed) return
    setError('')
    setResult(null)
    try {
      const next = await executeQuery({ query: trimmed, write: writeMode }).unwrap()
      setResult(next)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setConfirmOpen(false)
    }
  }, [executeQuery, query, writeMode])

  const handleRunClick = React.useCallback(() => {
    if (!canRun) return
    if (writeMode) {
      setConfirmOpen(true)
      return
    }
    void run()
  }, [canRun, run, writeMode])

  return (
    <div className="flex flex-col gap-card">
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-4">
          {embedded ? null : (
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-ink">Postgres query engine</h3>
              <p className="text-xs text-ink-3">
                Run SQL against the ontology application Postgres (the proxy&apos;s own pool).
                Queries are tenant-scoped by organization.
              </p>
            </div>
          )}

          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              Live application Postgres via the proxy. Read mode is SELECT-only; write mode can
              mutate operational tables. There is no undo.
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="settings-postgres-query" className="text-xs text-ink-2">
              SQL
            </Label>
            <Textarea
              id="settings-postgres-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault()
                  handleRunClick()
                }
              }}
              rows={12}
              spellCheck={false}
              placeholder={`SELECT id, name, state FROM custom_agent_configs ORDER BY created_at DESC LIMIT 50;\n-- Write mode required for DML/DDL`}
              className="font-mono text-13"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className={
                writeMode
                  ? 'border-st-warn/40 bg-st-warn-bg text-st-warn hover:bg-st-warn-bg hover:text-st-warn'
                  : undefined
              }
              onClick={handleRunClick}
              disabled={!canRun}
            >
              {executeState.isLoading ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Play className="fill-current" />
              )}
              {executeState.isLoading ? 'Running…' : 'Run'}
            </Button>
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-11 text-ink-3">
              <input
                type="checkbox"
                checked={writeMode}
                onChange={(event) => setWriteMode(event.target.checked)}
                className="size-3 cursor-pointer accent-st-warn"
              />
              Write mode
            </label>
            {writeMode ? (
              <span className="text-11 text-st-warn">DML/DDL will execute against Postgres.</span>
            ) : null}
            <span className="ml-auto text-11 text-ink-4">⌘↵ to run</span>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card className="gap-0 border-st-down/30 py-0 shadow-sm">
          <CardContent className="px-4 py-3 text-xs text-st-down" role="alert">
            {error}
          </CardContent>
        </Card>
      ) : null}

      {result ? <QueryResultCard result={result} /> : null}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Execute Postgres write?"
        description="This will run a mutating SQL statement against the ontology application database. There is no undo."
        confirmLabel="Execute write"
        destructive
        busy={executeState.isLoading}
        onConfirm={() => void run()}
      />
    </div>
  )
}

function QueryResultCard({ result }: { result: PostgresQueryResult }) {
  if (result.write) {
    return (
      <Card className="gap-0 py-0 shadow-sm">
        <CardContent className="px-4 py-3 text-sm text-ink-2">
          <span className="font-mono text-brand">
            {(result.rowsAffected ?? 0).toLocaleString()}
          </span>{' '}
          {(result.rowsAffected ?? 0) === 1 ? 'row' : 'rows'} affected.
        </CardContent>
      </Card>
    )
  }

  const columns = result.columns ?? []
  const rows = result.rows ?? []
  const truncated = result.truncated === true
  const displayRows = rows.length > RESULT_DISPLAY_CAP ? rows.slice(0, RESULT_DISPLAY_CAP) : rows

  if (columns.length === 0 && rows.length === 0) {
    return (
      <Card className="gap-0 py-0 shadow-sm">
        <CardContent className="px-4 py-3 text-sm text-ink-3">Query returned 0 rows.</CardContent>
      </Card>
    )
  }

  return (
    <Card className="gap-0 overflow-hidden py-0 shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2 text-11 text-ink-3">
        <span>
          <span className="font-medium text-ink-2">
            {(result.rowCount ?? rows.length).toLocaleString()}
          </span>{' '}
          {(result.rowCount ?? rows.length) === 1 ? 'row' : 'rows'}
          {truncated ? (
            <>
              {' · '}
              <span className="text-st-warn">truncated at server cap</span>
            </>
          ) : null}
        </span>
      </div>
      <div className="min-h-[12rem] max-h-[32rem] overflow-auto">
        <table className="w-full border-collapse text-left text-xs">
          <thead className="sticky top-0 z-10 bg-bg-sunk/95 text-ink-4 backdrop-blur">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="border-b border-line px-3 py-2 text-11 font-semibold uppercase tracking-[0.06em]"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => (
              <tr key={idx} className="border-b border-line last:border-b-0 hover:bg-bg-sunk/60">
                {columns.map((_, colIdx) => (
                  <td
                    key={colIdx}
                    className={cn(
                      'whitespace-nowrap px-3 py-1.5 font-mono text-11',
                      typeof row[colIdx] === 'number' ? 'text-brand' : 'text-ink-2'
                    )}
                  >
                    {formatCell(row[colIdx])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
